import { useInfiniteTikTokUserPostStats } from '@round/platforms/tiktok';
import { Card, SelectOption, tableHelpers } from '@round/creatorbase-ui';
import { ColumnDef } from '@tanstack/react-table';
import { GetTiktokUserPostStatsOrderingKeys, OrderingValues, TiktokUserPostStats } from '@round/api';
import { buildTiktokPostUrl, formatDateObjShort, numberWithCommas } from 'helpers';
import styles from './TopPosts.module.css';
import { isNumber } from 'utility/utility';
import VirtualizedTable from '../../../components/VirtualizedTable/VirtualizedTable';
import { ClientInfluencerAccount } from '../../../helpers';
import TableSortingSelect from '../../../components/TableSortingSelect/TableSortingSelect';
import cn from 'classnames';
import { useState } from 'react';
import TableEmptyState from '../../../components/TableEmptyState/TableEmptyState';

const { SkeletonCell, TruncatedLinkCell } = tableHelpers;

const sortingOptions: SelectOption<OrderingValues<GetTiktokUserPostStatsOrderingKeys>>[] = [
    { label: 'Most recent', value: '-create_time' },
    { label: 'Most views', value: '-play_count' },
];

type Props = {
    account: ClientInfluencerAccount | undefined | null;
    isLoading?: boolean;
};

const TikTokTopPosts = ({ account, isLoading }: Props) => {
    const userId = account?.creatorbase_user_id;
    const username = account?.platform_username;
    const [sorting, setSorting] = useState<OrderingValues<GetTiktokUserPostStatsOrderingKeys>>('-create_time');

    const {
        data,
        isLoading: arePostStatsLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteTikTokUserPostStats(
        { user_id: userId ?? 0, page_size: 25, ordering: sorting },
        { enabled: !!userId && !isLoading }
    );

    const meta = {
        isLoading: isLoading || arePostStatsLoading,
        isError: isError,
    } as const;

    const rows = tableHelpers.getLoadingRows(data?.pages.flatMap((page) => page.results) ?? [], {
        isLoading: meta.isLoading,
        pageSize: 10,
    });

    const columns: ColumnDef<tableHelpers.WithLoadingRow<TiktokUserPostStats>, any>[] = [
        {
            header: 'Description',
            id: 'description',
            meta: { className: styles.descriptionCell },
            size: 300,
            accessorFn: (row) => ({
                href: buildTiktokPostUrl(username ?? '', row.video_tiktok_id ?? ''),
                value: row.description || '-',
                tooltipContent: <div className={styles.descriptionCellTooltip}>{row.description || '-'}</div>,
            }),
            cell: TruncatedLinkCell,
        },
        {
            header: 'Date posted',
            id: 'created_time',
            meta: { className: styles.fadedCell },
            accessorFn: ({ create_time }) => (create_time ? formatDateObjShort(new Date(create_time * 1000)) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Location',
            id: 'location',
            meta: { className: styles.fadedCell },
            accessorFn: (row) => row.location || '-',
            cell: SkeletonCell,
        },
        {
            header: 'Boosted',
            id: 'is_ad',
            meta: { className: styles.fadedCell },
            accessorFn: (row) => (row.is_ad ? 'Yes' : 'No'),
            cell: SkeletonCell,
        },
        {
            header: 'Views',
            id: 'views',
            accessorFn: (row) => (isNumber(row.play_count) ? numberWithCommas(row.play_count) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Likes',
            id: 'likes',
            accessorFn: (row) => (isNumber(row.like_count) ? numberWithCommas(row.like_count) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Comments',
            id: 'comments',
            accessorFn: (row) => (isNumber(row.comment_count) ? numberWithCommas(row.comment_count) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Shares',
            accessorFn: (row) => (isNumber(row.share_count) ? numberWithCommas(row.share_count) : '-'),
            cell: SkeletonCell,
        },
        {
            header: 'Saves',
            accessorFn: (row) => (isNumber(row.save_count) ? numberWithCommas(row.save_count) : '-'),
            cell: SkeletonCell,
        },
    ];

    const noDataTitle = meta.isError ? 'Something went wrong' : 'No posts found';
    const noDataDescription = meta.isError
        ? 'Failed to load posts. Please try again.'
        : 'No posts found for this creator. Please try again later.';

    return (
        <Card size="sm" variant="borderless" className={styles.cardContainer}>
            <Card.Header className={cn(styles.cardHeader, styles.header)}>
                <Card.Title>Top Posts</Card.Title>
                <TableSortingSelect options={sortingOptions} value={sorting} onChange={setSorting} />
            </Card.Header>
            <Card.Content className={styles.cardContent}>
                <VirtualizedTable
                    meta={meta}
                    data={rows}
                    columns={columns}
                    endReachedThreshold={400}
                    onEndReached={() => {
                        if (meta.isLoading || !hasNextPage || isFetchingNextPage) {
                            return;
                        }

                        void fetchNextPage();
                    }}
                    virtualizerOptions={{
                        count: rows.length,
                        estimateSize: () => 50,
                    }}
                    noDataLabel={
                        <TableEmptyState isError={meta.isError} title={noDataTitle} description={noDataDescription} />
                    }
                />
            </Card.Content>
        </Card>
    );
};

export default TikTokTopPosts;
